
import { supabase } from '@/integrations/supabase/client';

export interface StuckExportCleanupResult {
  success: boolean;
  cleanedCount: number;
  error?: string;
}

/**
 * Clean up stuck exports that have been processing for too long
 * @param maxAgeMinutes Maximum age in minutes before considering an export stuck (default: 30 minutes - reduced for CPU limits)
 */
export const cleanupStuckExports = async (maxAgeMinutes: number = 30): Promise<StuckExportCleanupResult> => {
  try {
    const cutoffTime = new Date();
    cutoffTime.setMinutes(cutoffTime.getMinutes() - maxAgeMinutes);
        
    // Find exports that are stuck in processing or queued state for too long
    const { data: stuckExports, error: findError } = await supabase
      .from('csv_exports')
      .select('export_id, connection_id, table_name, created_at, started_at, current_step, processing_duration_ms')
      .in('status', ['queued', 'processing'])
      .lt('created_at', cutoffTime.toISOString());

    if (findError) {
      console.error('Error finding stuck exports:', findError);
      return { success: false, cleanedCount: 0, error: findError.message };
    }

    if (!stuckExports || stuckExports.length === 0) {
      return { success: true, cleanedCount: 0 };
    }

    // Mark these exports as failed with CPU timeout message
    const { error: updateError } = await supabase
      .from('csv_exports')
      .update({
        status: 'failed',
        error_message: `Export timeout: Processing exceeded ${maxAgeMinutes} minutes (likely CPU limit reached). Try using filters to reduce data size.`,
        completed_at: new Date().toISOString(),
        failure_step: 'CPU_TIMEOUT'
      })
      .in('status', ['queued', 'processing'])
      .lt('created_at', cutoffTime.toISOString());

    if (updateError) {
      console.error('Error updating stuck exports:', updateError);
      return { success: false, cleanedCount: 0, error: updateError.message };
    }

    return { success: true, cleanedCount: stuckExports.length };

  } catch (error) {
    console.error('Error in cleanup process:', error);
    return { 
      success: false, 
      cleanedCount: 0, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * Get details about a specific export for debugging - CPU optimized
 */
export const getExportDebugInfo = async (exportId: string) => {
  try {
    const { data, error } = await supabase
      .from('csv_exports')
      .select('*')
      .eq('export_id', exportId)
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    // Calculate processing duration with type safety
    const exportData = data as any;
    let processingDuration = exportData.processing_duration_ms;
    if (!processingDuration && exportData.started_at) {
      const startTime = new Date(exportData.started_at).getTime();
      const now = Date.now();
      processingDuration = now - startTime;
    }

    // Enhanced debugging info for CPU issues
    const isStuck = exportData.status === 'processing' && processingDuration && processingDuration > 1800000; // 30 minutes
    const isCpuTimeout = processingDuration && processingDuration > 600000; // 10 minutes (likely CPU limit)

    return {
      success: true,
      export: {
        ...exportData,
        calculated_processing_duration_ms: processingDuration,
        is_stuck: isStuck,
        likely_cpu_timeout: isCpuTimeout,
        cpu_optimization_suggestions: isCpuTimeout ? [
          'Use filters to reduce data size',
          'Export smaller date ranges',
          'Contact support for very large datasets'
        ] : []
      }
    };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * Force cleanup of all processing exports (emergency use)
 */
export const forceCleanupAllProcessingExports = async (): Promise<StuckExportCleanupResult> => {
  try {    
    const { data: processingExports, error: findError } = await supabase
      .from('csv_exports')
      .select('export_id, connection_id, table_name, created_at')
      .in('status', ['queued', 'processing']);

    if (findError) {
      return { success: false, cleanedCount: 0, error: findError.message };
    }

    if (!processingExports || processingExports.length === 0) {
      return { success: true, cleanedCount: 0 };
    }

    const { error: updateError } = await supabase
      .from('csv_exports')
      .update({
        status: 'failed',
        error_message: 'Export forcibly cancelled due to system CPU timeout issues. Please retry with smaller datasets or filters.',
        completed_at: new Date().toISOString(),
        failure_step: 'FORCE_CLEANUP'
      })
      .in('status', ['queued', 'processing']);

    if (updateError) {
      return { success: false, cleanedCount: 0, error: updateError.message };
    }

    return { success: true, cleanedCount: processingExports.length };

  } catch (error) {
    return { 
      success: false, 
      cleanedCount: 0, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * Initialize the export cleanup on app start
 */
export const initializeExportCleanup = async (): Promise<void> => {
  try {
    // Clean up any stuck exports on app start
    const result = await cleanupStuckExports(30);
  } catch (error) {
    console.error('Error during app startup export cleanup:', error);
  }
};
